import {Box, Group, Stack, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getExtensionIcon} from 'dev-platform/extensionIconMapper';

import {
  registryLabelsForExtensionHeader,
  type Extension,
  type ExtensionLabelSpec,
} from './extensionConstants';
import ExtensionTags from './ExtensionTags';

export const extensionIconStyles = StyleSheet.create({
  icon: {
    width: 48,
    height: 48,
    flexShrink: 0,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  iconImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  iconPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    fontSize: 28,
    borderRadius: 8,
  },
});

export interface ExtensionStoreExtensionHeaderProps {
  extension: Extension;
  /** Required to show registry label badges. Omit in contexts without label specs (e.g. install wizard). */
  labelSpecs?: ReadonlyArray<ExtensionLabelSpec>;
  /** Optional right column (e.g. Install). Omit for install wizard — same header without actions. */
  trailingSlot?: React.ReactNode;
}

/** Shared header for extension store details and install wizard (must stay visually identical). */
export default function ExtensionStoreExtensionHeader({
  extension,
  labelSpecs,
  trailingSlot,
}: ExtensionStoreExtensionHeaderProps): JSX.Element {
  const displayName = extension.displayName ?? extension.id;
  const {provider} = extension;
  const registryLabels = registryLabelsForExtensionHeader(extension.labels, labelSpecs ?? []);

  return (
    <Group align="flex-start" spacing="md">
      <Box className={css(extensionIconStyles.icon)}>
        {getExtensionIcon(extension.id, {
          className: css(extensionIconStyles.iconImg, extensionIconStyles.iconPlaceholder),
          extensionType: extension.extensionType,
        })}
      </Box>
      <Stack spacing="xs" style={{flex: 1, minWidth: 0}}>
        <Group spacing="xs" align="center" noWrap={true}>
          <Text fw={700} size="xl" component="span">
            {displayName}
          </Text>
          <Text size="sm" c="dimmed" component="span">
            by {provider.name}
          </Text>
        </Group>
        {extension.tagLine != null && extension.tagLine !== '' && <Text size="md">{extension.tagLine}</Text>}
        <Group spacing={4} mt="xs">
          <ExtensionTags extensionType={extension.extensionType} registryLabels={registryLabels} />
        </Group>
      </Stack>
      {trailingSlot}
    </Group>
  );
}
